import type { ReactNode, SVGProps } from 'react';

/**
 * The icon set.
 *
 * Inline SVG on a 24px grid, Lucide geometry (ISC licensed), no runtime
 * dependency. That is the standard v1 set already, and it is kept deliberately:
 * a financial product should not pull an icon package into its bundle, and
 * these tree-shake to exactly what is imported.
 *
 * ── SIZING ────────────────────────────────────────────────────────────────
 *
 * Default 16px, not 24. Both references use small icons beside 12–13px text,
 * and an icon that out-weighs its label is the fastest way to make a dense
 * table look noisy. `strokeWidth` scales inversely with size so a 24px icon
 * does not read heavier than a 14px one.
 *
 * Colour comes from `currentColor` always. An icon never picks its own colour —
 * it inherits from whatever it sits inside, so a ghost button, a danger banner
 * and an active nav item all get the right one for free.
 */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'stroke' | 'children'> {
  size?: number;
  strokeWidth?: number;
}

function Svg({ size = 16, strokeWidth, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      // Optical correction: stroke width is specified in the 24-unit viewBox,
      // so a 14px icon at strokeWidth 2 renders a 1.17px line while a 24px one
      // renders 2px. Scaling keeps the apparent weight constant across sizes.
      strokeWidth={strokeWidth ?? (size <= 16 ? 1.75 : size <= 20 ? 1.6 : 1.5)}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Icons in this system are always decorative — every one sits beside a
      // text label, or inside a control that carries its own accessible name.
      // Marking them hidden stops screen readers announcing them twice.
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ─────────────────────────────── navigation ─────────────────────────────── */

export const IconBox = (p: IconProps) => (
  <Svg {...p}><path d="M21 8.5v7l-9 5-9-5v-7l9-5z" /><path d="M3.5 8.5 12 13l8.5-4.5" /><path d="M12 13v8" /></Svg>
);
export const IconStamp = (p: IconProps) => (
  <Svg {...p}><path d="M6 21h12" /><path d="M7.5 17.5h9v1.8h-9z" /><path d="M9 17.5v-2.2c0-1-.7-1.6-1.3-2.3A4.5 4.5 0 0 1 12 5.5a4.5 4.5 0 0 1 3.3 7.5c-.6.7-1.3 1.3-1.3 2.3v2.2" /></Svg>
);

export const IconSearch = (p: IconProps) => (
  <Svg {...p}><circle cx="10.5" cy="10.5" r="6.5" /><path d="M20 20l-4.5-4.5" /></Svg>
);
export const IconFilter = (p: IconProps) => (
  <Svg {...p}><path d="M3.5 5.5h17" /><path d="M6.5 12h11" /><path d="M10 18.5h4" /></Svg>
);
export const IconChevronDown = (p: IconProps) => (
  <Svg {...p}><path d="M6 9.5 12 15l6-5.5" /></Svg>
);
export const IconChevronLeft = (p: IconProps) => (
  <Svg {...p}><path d="M14.5 6 9 12l5.5 6" /></Svg>
);
export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}><path d="M9.5 6 15 12l-5.5 6" /></Svg>
);
export const IconArrowUp = (p: IconProps) => (
  <Svg {...p}><path d="M12 19V5" /><path d="M6.5 10.5 12 5l5.5 5.5" /></Svg>
);
export const IconCheck = (p: IconProps) => (
  <Svg {...p}><path d="M4.5 12.5 9.5 17.5 19.5 6.5" /></Svg>
);
export const IconX = (p: IconProps) => (
  <Svg {...p}><path d="M6 6l12 12M18 6 6 18" /></Svg>
);
export const IconInfo = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><circle cx="12" cy="7.8" r="1" fill="currentColor" stroke="none" /></Svg>
);
export const IconAlert = (p: IconProps) => (
  <Svg {...p}><path d="M10.3 3.9 2.5 17.5A2 2 0 0 0 4.2 20.5h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0" /><path d="M12 9.5v4" /><circle cx="12" cy="16.8" r="1" fill="currentColor" stroke="none" /></Svg>
);
export const IconAlertCircle = (p: IconProps) => (
  <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5" /><circle cx="12" cy="16.2" r="1" fill="currentColor" stroke="none" /></Svg>
);
export const IconLock = (p: IconProps) => (
  <Svg {...p}><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></Svg>
);
